export interface RegistrationStream {
  id: string;
  name: string;
  /** FK link — the only record of a stream's registration. */
  registrationId?: string | null;
  /** How the link was made. Ranked when pre-selecting a source; see below. */
  registrationLinkSource?: string | null;
  /**
   * The linked registration itself, used only to label the option in
   * RegistrationSourcePicker. Optional so callers that only need the
   * conflict/ranking logic don't have to select it.
   */
  registration?: {
    stateRegisteredId?: string | null;
    status?: string | null;
    registeredAt?: string | null;
  } | null;
}

// Mirrors LINK_SOURCE_RANK in the server's resolveMergedRegistration: a human's
// link outranks an importer's, which outranks the matcher's. Kept in step so the
// source this picker pre-selects is the one the server would have chosen anyway.
const LINK_SOURCE_RANK: Record<string, number> = {
  AUTO: 0,
  IMPORT: 1,
  MANUAL: 2,
};

function linkRank(source: string | null | undefined): number {
  if (source == null) return -1;
  return LINK_SOURCE_RANK[source] ?? -1;
}

/** Streams linked to a registration. */
export function getRegistrationCandidates(streams: RegistrationStream[]): RegistrationStream[] {
  return streams.filter((stream) => stream.registrationId != null);
}

/**
 * True when the linked streams point at different registrations and the user must
 * pick which one survives. False when none or one is linked, or when all linked
 * streams share a registration — differing link sources are not a conflict, since
 * the server resolves the surviving source itself.
 */
export function needsRegistrationChoice(streams: RegistrationStream[]): boolean {
  const candidates = getRegistrationCandidates(streams);
  if (candidates.length < 2) return false;
  return new Set(candidates.map((stream) => stream.registrationId)).size > 1;
}

/** The id of the linked stream whose registration should be pre-selected. */
export function defaultRegistrationSourceId(streams: RegistrationStream[]): string | undefined {
  const candidates = getRegistrationCandidates(streams);
  if (candidates.length === 0) return undefined;
  const winner = candidates.reduce((best, candidate) =>
    linkRank(candidate.registrationLinkSource) > linkRank(best.registrationLinkSource)
      ? candidate
      : best
  );
  return winner.id;
}
